<script setup lang="ts">
import { CloudPbxSchema } from '~/schemas/cloud-pbx';
import { stats } from '~/data/stats';

const { data: page } = await useAsyncData(() => 
  queryCollection('cloudPbxPage').first()
);

useSeoMeta({
  title: page.value?.metaTitle,
  description: page.value?.metaDescription,
});

useHead({
  link: [
    { rel: "canonical", href: page.value?.linkCanonical },
  ],
  script: [
    {
      type: 'application/ld+json',
      textContent: JSON.stringify(CloudPbxSchema)
    },
  ],
});


const modalsStore = useModalsStore();
const windowScroll = useWindowScroll();
const hero = useTemplateRef('hero');
const heroLeftImg = useTemplateRef('heroLeftImg');
const heroRightImg = useTemplateRef('heroRightImg');
const heroStats = useTemplateRef('heroStats');
const device = useDevice();

function handleOpenModal() {
  modalsStore.openModal('CTA');
};

function handleOnClickViewPrices() {
  const elem = document.getElementById('prices');
  elem?.scrollIntoView({ behavior: 'smooth' });
};

function parallaxHeroImgs(scrollY: number) {
  if (!heroLeftImg.value || !heroRightImg.value || !heroStats.value || !hero.value) return;

  const endParallax = 530;
  const progress = scrollY < endParallax ? scrollY : endParallax;
  const statsStartPosition = hero.value.offsetHeight - (heroStats.value.offsetHeight + 10) * 2;

  heroStats.value.style.transform = `translate(0, ${ progress * 0.5 + statsStartPosition }px)`;
  heroLeftImg.value.imgEl.style.transform = `translate(${ progress * 0.3 }px, ${ progress * 0.5 }px)`;
  heroRightImg.value.imgEl.style.transform = `translate(${ -progress * 0.3 }px, ${ progress * 0.3 }px)`;
};

watch(windowScroll.y, (newVal) => {
  if (!device.isDesktop.value) return;
  parallaxHeroImgs(newVal);
});
</script>

<template>
  <template v-if="page">
    <SectionsBase class="overflow-hidden pb-44! lg:pb-38! pt-0! relative">
      <div ref="hero" class="bg-linear-to-b from-primary-800 to-primary-500 relative isolate overflow-hidden pt-25 lg:pt-26 z-1">
        <div class="container relative z-2">
          <div class="w-full flex flex-col items-center gap-10">
            <div>
              <h1 class="text-secondary-600 highlight text-center">{{ page.title }}</h1>
              <span class="text-white h1"><span class="semicolon">؛</span> {{ page.subTitle }}</span>
            </div>
            <p class="text-body1-semibold text-primary-50 text-center">{{ page.description }}</p>
            <div class="w-full flex-center flex-col lg:flex-row gap-4">
              <Button
                variant="white"
                :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
                size="Large"
                pill
                @click="handleOpenModal"
                class="relative w-46.5"
              >
                <span>رایگان امتحان کنید</span>
                <IconsCurvedArrow 
                  class="w-30 text-secondary-600 absolute scale-y-[-1] origin-bottom bottom-20 -right-18 -rotate-30 hidden
                    lg:block pointer-events-none" 
                />
              </Button>
              <Button
                type="Outline"
                variant="white"
                :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
                size="Large"
                pill
                class="w-46.5 [&_.btn-slot-parent]:w-full [&_.btn-slot-parent]:justify-between!"
                @click="handleOnClickViewPrices"
              >
                مشاهده تعرفه‌ها
              </Button>
            </div>
          </div>
          <div class="w-full flex-center mt-22.5 relative">
            <NuxtImg
              width="700"
              height="420"
              :src="page.img"
              :alt="page.title"
              sizes="200px md:400px lg:800px"
              :preload="{ fetchPriority: 'high' }"
              fetchpriority="high"
              loading="eager"
              class="rounded-lg lg:rounded-3xl w-75 lg:w-175"
            />
            <NuxtImg
              ref="heroRightImg"
              width="140"
              src="/img/new/cloud-pbx/hero-03.webp"
              :alt="page.title"
              :preload="{ fetchPriority: 'high' }"
              fetchpriority="high"
              loading="eager"
              class="rounded-[7px] lg:rounded-3xl w-17.5 lg:w-35 absolute bottom-5 lg:bottom-auto lg:-top-10 -right-1
                lg:right-1/10 filter-[drop-shadow(0_20px_40px_rgba(0,0,0,0.25))]"
            />
            <NuxtImg
              ref="heroLeftImg"
              width="300"
              src="/img/new/cloud-pbx/hero-02.webp"
              :alt="page.title"
              :preload="{ fetchPriority: 'high' }"
              fetchpriority="high"
              loading="eager"
              class="w-33.75 lg:w-75 absolute bottom-20 lg:bottom-auto lg:-top-10 -left-2 lg:left-0 
                filter-[drop-shadow(0_20px_40px_rgba(0,0,0,0.25))]"
            />
          </div>
        </div>
        <NuxtImg
          src="/img/new/cloud/03.webp"
          alt="تلفن ابری"
          loading="lazy"
          class="absolute w-75 lg:w-105 top-50 lg:top-40 -right-30 mix-blend-screen cloud-anim-1"
        />
        <NuxtImg
          src="/img/new/cloud/03.webp"
          alt="تلفن ابری"
          loading="lazy"
          class="absolute w-75 lg:w-120 top-85 lg:top-40 -left-20 lg:-left-40 mix-blend-screen cloud-anim-2"
        />
      </div>
      <div ref="heroStats" class="stats">
        <div 
          v-for="item, index in stats.navatel.slice(0, 4)" 
          :key="index"
          class="text-white flex-center flex-col"
        >
          <p class="h1 font-semibold">
            <span>{{ item.number.toLocaleString() }}<span v-if="item.isPercent">%</span></span>
            <span v-if="item.symbol" class="text-secondary-600">{{ item.symbol }}</span>
          </p>
          <p class="text-body2">{{ item.title }}</p>
        </div>
      </div>
    </SectionsBase>

    <LazySectionsAboutCloudPbx
      :title="page.about.title"
      :desc="page.about.desc"
    />

    <LazyIdleSectionsLogos hydrate-on-idle />

    <LazyVisibleTOC hydrate-on-visible :topics="page.topics" />

    <LazyVisibleSectionsFeaturesCloudPbx
      hydrate-on-visible
      :title="page.features.title"
      :desc="page.features.desc"
      :items="page.features.items"    
    />

    <LazySectionsFacilitiesCloudPbx
      :title="page.facilities.title"
      :desc="page.facilities.desc"
      :items="page.facilities.items"
    />

    <LazyVisibleSectionsLaunchSteps
      hydrate-on-visible
      :title="page.launchSteps.title"
      :desc="page.launchSteps.desc"
      :items="page.launchSteps.items"    
    />

    <LazyVisibleSectionsWhyNavatel
      hydrate-on-visible
      :title="page.whyNavatel.title"
      :desc="page.whyNavatel.desc"
      :items="page.whyNavatel.items"
    />

    <LazySectionsSolutionsCarousel
      :title="page.solutions.title"
      :desc="page.solutions.desc"
      :items="page.solutions.items"
    />

    <LazyVisibleSectionsRelatedProductsShowcase
      hydrate-on-visible
      :title="page.relatedProducts.title"
      :desc="page.relatedProducts.desc"
      :items="page.relatedProducts.items"
    />

    <LazyVisibleSectionsPricesCloudPbx
      hydrate-on-visible
      :title="page.prices.title"
      :desc="page.prices.desc"
    />

    <LazyVisibleSectionsComments hydrate-on-visible />

    <SectionsFAQ
      :title="page.FAQ.title"
      :items="page.FAQ.items"
    />

    <LazyVisibleSectionsCTA 
      hydrate-on-visible 
      :title="page.CTA.title" 
      :desc="page.CTA.desc"
    />
  </template>
</template>

<style scoped>
@reference "~/assets/css/main.css";

.stats {
  @apply
  absolute w-max bottom-0 lg:bottom-auto lg:top-0 right-1/2 translate-x-1/2 bg-primary-500 grid grid-cols-2 lg:grid-cols-4 
  z-0 rounded-b-[20px] pb-5 pt-10 px-1 lg:px-6 gap-y-5 gap-x-3
}
.stats::before, .stats::after {
  @apply
  content-[''] absolute top-1 w-20 h-20
}
.stats::before {
  @apply
  -right-20 rounded-tl-[40px] shadow-[-30px_-30px_var(--color-primary-500)] z-[-1]
}
.stats::after {
  @apply
  -left-20 rounded-tr-[40px] shadow-[30px_-30px_var(--color-primary-500)] z-[-1]
}

@media screen and (min-width: 1024px) {
 .cloud-anim-1 {
    animation: cloud-move-1 60s linear infinite alternate;
  }
  .cloud-anim-2 {
    animation: cloud-move-2 60s linear infinite alternate;
  } 
}

@keyframes cloud-move-1 {
  0% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(-120px, -100px);
  }
  50% {
    transform: translate(-260px, 15px);
  }
  75% {
    transform: translate(-420px, 80px);
  }
  100% {
    transform: translate(-600px, 0);
  }
}
@keyframes cloud-move-2 {
  0% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(120px, 100px);
  }
  50% {
    transform: translate(260px, -15px);
  }
  75% {
    transform: translate(420px, -80px);
  }
  100% {
    transform: translate(600px, 0);
  }
}
</style>